import { defineStore } from 'pinia';
import http from '../api/httpRequest.js'
import useUserStore from './userStore.js'

const DARK_MODE_KEY = 'darkMode';
const LOCK_KEY_PREFIX = 'imLocked_';

// 锁屏状态按用户id持久化，防止刷新页面绕过锁屏
const lockKey = () => {
	const info = useUserStore().userInfo;
	const uid = info && info.id;
	return LOCK_KEY_PREFIX + (uid || 'anon');
}

// 应用主题到 html 根元素
const applyThemeClass = (dark) => {
	if (dark) {
		document.documentElement.classList.add('theme-dark');
	} else {
		document.documentElement.classList.remove('theme-dark');
	}
}

export default defineStore('configStore', {
	state: () => {
		return {
			fullScreen: true, // 当前是否全屏
			webrtc: {},
			darkMode: localStorage.getItem(DARK_MODE_KEY) === 'true', // 黑夜模式
			locked: false, // 是否处于锁屏状态
			chatWiped: false, // 服务端是否处于远程清理状态
			chatWipeSeq: 0, // 远程清理/恢复序号
			// 商用化功能开关(默认全开=运营行为;商用部署后端关闭后前端隐藏对应入口)
			features: {
				meal: true,
				coiai: true,
				report: true,
				legacyPhone: true,
				blacklistWrite: true,
				attendance: true,
				assignPhoneRequired: true,
				offlineLock: true,
				social: false // 发帖功能默认关闭(红线): 仅后端 /public/config/features 下发 true 时, 前端才显示内部大群发帖入口
			},
			brand: '' // 品牌名(后端下发)
		}
	},
	actions: {
		setConfig(config) {
			this.webrtc = config.webrtc;
			this.chatWiped = !!config.chatWiped;
			this.chatWipeSeq = config.chatWipeSeq || 0;
		},
		setFullScreen(fullScreen) {
			this.fullScreen = fullScreen;
		},
		setDarkMode(dark) {
			this.darkMode = dark;
			localStorage.setItem(DARK_MODE_KEY, dark ? 'true' : 'false');
			applyThemeClass(dark);
		},
		// 应用已保存的主题（app启动时调用）
		initTheme() {
			applyThemeClass(this.darkMode);
		},
		setLocked(locked) {
			this.locked = locked;
			try {
				if (locked) {
					localStorage.setItem(lockKey(), 'true');
				} else {
					localStorage.removeItem(lockKey());
				}
			} catch (e) {
				console.log('持久化锁屏状态失败', e);
			}
		},
		// 应用启动时根据持久化状态恢复锁屏（刷新后仍保持锁屏，不可绕过）
		restoreLock() {
			try {
				const shouldLock = localStorage.getItem(lockKey()) === 'true';
				if (shouldLock) {
					// 安全兜底：若密码哈希已丢失(老用户同步异常)，不锁屏，防止永久死锁
					const info = useUserStore().userInfo;
					const uid = info && info.id;
					const hasPwd = !!localStorage.getItem('lockPwd_' + uid);
					if (!hasPwd) {
						localStorage.removeItem(lockKey());
						this.locked = false;
						return;
					}
				}
				this.locked = shouldLock;
			} catch (e) {
				this.locked = false;
			}
		},
		async loadConfig() {
			const config = await http({
				url: '/system/config',
				method: 'get'
			});
			console.log("系统配置", config)
			this.setConfig(config);
		},
		// 拉取商用化功能开关与品牌(免登录接口;失败时保持默认全开,不影响运营)
		async loadFeatures() {
			try {
				const data = await http({
					url: '/public/config/features',
					method: 'get'
				});
				if (data) {
					this.features = {
						meal: data.meal !== false,
						coiai: data.coiai !== false,
						report: data.report !== false,
						legacyPhone: data.legacyPhone !== false,
						blacklistWrite: data.blacklistWrite !== false,
						attendance: data.attendance !== false,
						assignPhoneRequired: data.assignPhoneRequired !== false,
						offlineLock: data.offlineLock !== false,
						social: data.social === true
					};
					this.brand = data.brand || '';
					if (data.brand) {
						document.title = data.brand;
					}
				}
			} catch (e) {
				console.log('加载功能开关失败,保持默认', e);
			}
		}
	}
});
